import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaPray,
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaWhatsapp,
  FaRupeeSign,
  FaQrcode,
  FaCopy,
  FaCheck,
  FaUpload,
  FaSpinner,
  FaCheckCircle,
  FaArrowLeft,
  FaArrowRight,
  FaOm,
  FaReceipt,
} from "react-icons/fa";
import qrCodeImg from "../assets/qr-code.jpeg";
import { API_ENDPOINTS } from "../config/api";
import SathyanarayanaReceipt from "./SathyanarayanaReceipt";
import "../styles/PoojaBookingModal.css";

const templeUpiId = "gnanasaiguruji@okhdfcbank";
const templePayee = "Parasuram Guruji S";

const DEFAULT_POOJAS_LIST = [
  "Sathya Narayana Pooja",
  "108 Kalasams Blessing",
  "Gomata Darshan Miracle",
  "Dhuni Pooja",
  "Santhana Bhagya Pooja",
  "Daily Gho Pooja",
  "Amavasya Special Pooja",
  "Lord Murugan Divine Presence",
];

const PoojaBookingModal = ({
  isOpen,
  onClose,
  initialPoojaName = "Sathya Narayana Pooja",
  poojaList = [],
}) => {
  // Step state: 1 = Registration, 2 = QR Payment, 3 = Confirmation
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copiedUpi, setCopiedUpi] = useState(false);

  // Combine default pooja names with any dynamically fetched poojas
  const allPoojaNames = Array.from(
    new Set([
      ...DEFAULT_POOJAS_LIST,
      ...(poojaList || []).map((p) => p?.name).filter(Boolean),
    ])
  );

  // Get tomorrow's date for default date picker
  const getTomorrowDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  // Form state
  const [formData, setFormData] = useState({
    poojaName: initialPoojaName || "Sathya Narayana Pooja",
    devoteeName: "",
    email: "",
    phoneNumber: "",
    whatsappNumber: "",
    poojaDate: getTomorrowDate(),
    gothram: "",
    rasi: "",
    nakshatra: "",
    address: "",
    sankalpamWishes: "",
    amount: 1000,
    customAmount: "",
    transactionId: "",
  });

  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState("");
  const [submittedBooking, setSubmittedBooking] = useState(null);
  const [showOfficialReceipt, setShowOfficialReceipt] = useState(false);
  const [formError, setFormError] = useState("");

  // Sync initial pooja name when modal opens or prop changes
  useEffect(() => {
    if (initialPoojaName) {
      setFormData((prev) => ({
        ...prev,
        poojaName: initialPoojaName,
      }));
    }
  }, [initialPoojaName, isOpen]);

  // Reset modal state when closing
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setScreenshotFile(null);
      setScreenshotPreview("");
      setSubmittedBooking(null);
      setShowOfficialReceipt(false);
      setFormError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      // When mobile / phone number is filled by user, auto-fill whatsapp number
      if (name === "phoneNumber") {
        updated.whatsappNumber = value;
      }
      return updated;
    });
    setFormError("");
  };

  const handleScreenshotSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshotFile(file);
      setScreenshotPreview(URL.createObjectURL(file));
      setFormError("");
    }
  };

  const handleCopyUpi = () => {
    navigator.clipboard.writeText(templeUpiId);
    setCopiedUpi(true);
    setTimeout(() => setCopiedUpi(false), 2000);
  };

  // Step 1 Validation -> Proceed to Payment
  const handleProceedToPayment = (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.poojaName.trim()) {
      setFormError("Please select a sacred pooja.");
      return;
    }
    if (!formData.devoteeName.trim()) {
      setFormError("Please enter the devotee name.");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      setFormError("Please provide a valid email address for booking updates.");
      return;
    }
    const cleanPhone = formData.phoneNumber.replace(/\D/g, "");
    if (cleanPhone.length < 10) {
      setFormError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!formData.poojaDate) {
      setFormError("Please select your preferred pooja date.");
      return;
    }
    if (!formData.address.trim()) {
      setFormError("Please enter the devotee city / address.");
      return;
    }

    setStep(2);
  };

  // Step 2 Submission -> Upload Screenshot & Create Booking
  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.transactionId.trim()) {
      setFormError("Please enter your UPI Transaction ID / UTR Number.");
      return;
    }

    setLoading(true);

    try {
      let uploadedScreenshotUrl = "";

      // 1. Upload screenshot if selected
      if (screenshotFile) {
        const uploadData = new FormData();
        uploadData.append("image", screenshotFile);

        const uploadRes = await fetch(API_ENDPOINTS.UPLOAD, {
          method: "POST",
          body: uploadData,
        });

        const uploadResult = await uploadRes.json();
        if (uploadRes.ok && uploadResult.imageUrl) {
          uploadedScreenshotUrl = uploadResult.imageUrl;
        }
      }

      // 2. Submit booking to backend
      const bookingPayload = {
        poojaName: formData.poojaName,
        devoteeName: formData.devoteeName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        whatsappNumber: formData.whatsappNumber || formData.phoneNumber,
        poojaDate: formData.poojaDate,
        gothram: formData.gothram,
        rasi: formData.rasi,
        nakshatra: formData.nakshatra,
        address: formData.address,
        sankalpamWishes: formData.sankalpamWishes,
        amount: Number(formData.amount),
        transactionId: formData.transactionId,
        paymentScreenshot: uploadedScreenshotUrl,
      };

      const res = await fetch(API_ENDPOINTS.POOJA_BOOKINGS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingPayload),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSubmittedBooking(data.booking);
        setStep(3);
      } else {
        setFormError(data.message || "Failed to submit pooja booking. Please try again.");
      }
    } catch (err) {
      console.error("Booking error:", err);
      setFormError("Network error occurred while submitting your booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pooja-modal-overlay">
      <div
        className="pooja-modal-card"
        role="dialog"
        aria-modal="true"
      >
        {/* MODAL HEADER */}
        <div className="pooja-modal-header">
          <div className="header-title-box">
            <span className="temple-om-tag">
              <FaOm /> SRI GNANA SAI BABA MANDIR
            </span>
            <h2>
              {step === 3
                ? "Sacred Registration Received"
                : step === 2
                ? "Temple QR Code Seva Dakshina"
                : "Book Sacred Temple Pooja"}
            </h2>
            <p className="header-sub">
              {step === 3
                ? "May Bhagavan Sri Gnana Sai Baba shower divine blessings upon you"
                : step === 2
                ? `Complete Seva Dakshina of ₹${formData.amount || 1000} via UPI QR Code`
                : "Fill in devotee sankalpam details for the sacred ritual"}
            </p>
          </div>

          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* STEP PROGRESS INDICATOR */}
        {step !== 3 && (
          <div className="pooja-step-indicator">
            <div className={`step-item ${step >= 1 ? "active" : ""}`}>
              <span className="step-num">1</span>
              <span className="step-label">Devotee Sankalpam</span>
            </div>
            <div className={`step-divider ${step >= 2 ? "active" : ""}`} />
            <div className={`step-item ${step >= 2 ? "active" : ""}`}>
              <span className="step-num">2</span>
              <span className="step-label">QR Code Payment</span>
            </div>
          </div>
        )}

        {/* ERROR BANNER */}
        {formError && (
          <div className="pooja-modal-error">
            <span>⚠️ {formError}</span>
          </div>
        )}

        {/* ================= STEP 1: DEVOTEE REGISTRATION ================= */}
        {step === 1 && (
          <form onSubmit={handleProceedToPayment} className="pooja-modal-body">
            {/* POOJA SELECTION AUTOFILL */}
            <div className="pooja-form-group highlighted-pooja-group">
              <label>
                <FaPray className="field-icon" /> Select Temple Pooja *
              </label>
              <select
                name="poojaName"
                value={formData.poojaName}
                onChange={handleInputChange}
                className="pooja-form-select"
                required
              >
                {allPoojaNames.map((name, idx) => (
                  <option key={idx} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <small className="field-hint">
                ✨ Selected pooja is autofilled. You can switch rituals anytime.
              </small>
            </div>

            {/* DEVOTEE NAME & EMAIL */}
            <div className="pooja-form-row">
              <div className="pooja-form-group">
                <label>
                  <FaUser className="field-icon" /> Devotee Full Name *
                </label>
                <input
                  type="text"
                  name="devoteeName"
                  placeholder="Enter your full name"
                  value={formData.devoteeName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="pooja-form-group">
                <label>
                  <FaEnvelope className="field-icon" /> Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="example@gmail.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <small className="field-hint">
                  Approval & confirmation status will be sent here
                </small>
              </div>
            </div>

            {/* PHONE & WHATSAPP */}
            <div className="pooja-form-row">
              <div className="pooja-form-group">
                <label>
                  <FaPhoneAlt className="field-icon" /> Mobile Number *
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  placeholder="10-digit mobile number"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="pooja-form-group">
                <label>
                  <FaWhatsapp className="field-icon" /> WhatsApp Number
                </label>
                <input
                  type="tel"
                  name="whatsappNumber"
                  placeholder="Defaults to mobile number"
                  value={formData.whatsappNumber}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* POOJA DATE & GOTHRAM */}
            <div className="pooja-form-row">
              <div className="pooja-form-group">
                <label>
                  <FaCalendarAlt className="field-icon" /> Preferred Pooja Date *
                </label>
                <input
                  type="date"
                  name="poojaDate"
                  min={new Date().toISOString().split("T")[0]}
                  value={formData.poojaDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="pooja-form-group">
                <label>Gothram (Optional)</label>
                <input
                  type="text"
                  name="gothram"
                  placeholder="e.g. Bharadwaja, Kasyapa"
                  value={formData.gothram}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* RASI / NAKSHATRA & RESIDENTIAL CITY */}
            <div className="pooja-form-row">
              <div className="pooja-form-group">
                <label>Rasi & Nakshatra (Optional)</label>
                <input
                  type="text"
                  name="rasi"
                  placeholder="e.g. Mesham / Aswini"
                  value={formData.rasi}
                  onChange={handleInputChange}
                />
              </div>

              <div className="pooja-form-group">
                <label>Devotee City / Address *</label>
                <input
                  type="text"
                  name="address"
                  placeholder="e.g. Ambattur, Chennai"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* ACTION BUTTON */}
            <div className="pooja-modal-footer">
              <button
                type="button"
                className="modal-secondary-btn"
                onClick={onClose}
              >
                Cancel
              </button>

              <button type="submit" className="modal-primary-btn">
                <span>Proceed to QR Payment</span>
                <FaArrowRight />
              </button>
            </div>
          </form>
        )}

        {/* ================= STEP 2: TEMPLE QR PAYMENT ================= */}
        {step === 2 && (
          <form onSubmit={handleSubmitBooking} className="pooja-modal-body">
            <div className="payment-step-intro">
              <div className="payment-amount-banner">
                <span>Pooja: <strong>{formData.poojaName}</strong></span>
                <span className="pay-tag">
                  Seva Dakshina: <strong>₹{Number(formData.amount || 1000).toLocaleString("en-IN")}</strong>
                </span>
              </div>
            </div>

            {/* QR CODE & UPI CONTAINER */}
            <div className="modal-qr-container">
              <div className="modal-qr-frame">
                <img
                  src={qrCodeImg}
                  alt="Sri Gnana Sai Baba QR Code"
                  className="modal-qr-img"
                />
              </div>

              <div className="modal-qr-info">
                <div className="payee-card">
                  <div className="payee-title">
                    Payee: <strong>{templePayee}</strong>
                  </div>
                  <div className="upi-copy-row">
                    <span className="upi-id-text">{templeUpiId}</span>
                    <button
                      type="button"
                      onClick={handleCopyUpi}
                      className={`modal-copy-btn ${copiedUpi ? "copied" : ""}`}
                    >
                      {copiedUpi ? <FaCheck /> : <FaCopy />}
                      <span>{copiedUpi ? "Copied" : "Copy UPI"}</span>
                    </button>
                  </div>
                </div>

                <div className="payment-steps-list">
                  <ol>
                    <li>Open <strong>GPay</strong>, <strong>PhonePe</strong>, <strong>Paytm</strong>, or any UPI app.</li>
                    <li>Scan the QR code or send to UPI ID: <strong>{templeUpiId}</strong>.</li>
                    <li>The Seva Dakshina of <strong>₹1,000</strong> is automatically pre-filled in your UPI app.</li>
                    <li>Complete the payment and copy the <strong>UTR / Payment Reference Number</strong>.</li>
                    <li>Enter your UTR number below and submit your booking.</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* AMOUNT & UTR ROW */}
            <div className="pooja-form-row">
              <div className="pooja-form-group">
                <label>
                  <FaRupeeSign className="field-icon" /> Seva Dakshina Amount (Fixed)
                </label>
                <div className="static-dakshina-display">
                  <span className="static-dakshina-amount">₹1,000</span>
                  <span className="static-dakshina-tag">Pre-filled in QR</span>
                </div>
              </div>

              <div className="pooja-form-group">
                <label>
                  <FaQrcode className="field-icon" /> Transaction ID / UTR Number *
                </label>
                <input
                  type="text"
                  name="transactionId"
                  placeholder="e.g. 423891028492"
                  value={formData.transactionId}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* PAYMENT SCREENSHOT UPLOAD */}
            <div className="pooja-form-group">
              <label>
                <FaUpload className="field-icon" /> Upload Payment Receipt Screenshot (Optional)
              </label>
              <div className="screenshot-upload-box">
                <input
                  type="file"
                  id="pooja-screenshot-file"
                  accept="image/*"
                  onChange={handleScreenshotSelect}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="pooja-screenshot-file"
                  className="upload-dropzone-label"
                >
                  <FaUpload className="drop-icon" />
                  <span>
                    {screenshotFile
                      ? `Selected: ${screenshotFile.name}`
                      : "Click to browse & upload payment receipt screenshot"}
                  </span>
                </label>

                {screenshotPreview && (
                  <div className="screenshot-mini-preview">
                    <img src={screenshotPreview} alt="Screenshot preview" />
                    <button
                      type="button"
                      className="remove-thumb-btn"
                      onClick={() => {
                        setScreenshotFile(null);
                        setScreenshotPreview("");
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER ACTIONS */}
            <div className="pooja-modal-footer">
              <button
                type="button"
                className="modal-secondary-btn"
                onClick={() => setStep(1)}
                disabled={loading}
              >
                <FaArrowLeft />
                <span>Back to Details</span>
              </button>

              <button
                type="submit"
                className="modal-primary-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spin-icon" />
                    <span>Submitting Booking...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Booking & Verify</span>
                    <FaCheck />
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* ================= STEP 3: SUBMISSION CONFIRMATION ================= */}
        {step === 3 && submittedBooking && (
          <div className="pooja-modal-body success-body">
            <div className="success-icon-badge">
              <FaCheckCircle className="big-check" />
            </div>

            <h3>Registration Received Successfully!</h3>
            <p className="success-sub">
              Om Sai Ram, <strong>{submittedBooking.devoteeName}</strong>! Your sacred pooja booking has been registered.
            </p>

            <div className="booking-receipt-box">
              <div className="receipt-row">
                <span className="receipt-lbl">Booking Reference</span>
                <strong className="receipt-token">{submittedBooking.bookingId}</strong>
              </div>
              <div className="receipt-row">
                <span className="receipt-lbl">Sacred Ritual</span>
                <span>{submittedBooking.poojaName}</span>
              </div>
              <div className="receipt-row">
                <span className="receipt-lbl">Scheduled Date</span>
                <span>{submittedBooking.poojaDate}</span>
              </div>
              <div className="receipt-row">
                <span className="receipt-lbl">Seva Dakshina</span>
                <strong style={{ color: "#059669" }}>₹{submittedBooking.amount}</strong>
              </div>
              <div className="receipt-row">
                <span className="receipt-lbl">Submitted UTR</span>
                <code>{submittedBooking.transactionId}</code>
              </div>
              <div className="receipt-row">
                <span className="receipt-lbl">Current Status</span>
                <span className="receipt-status-pending">Pending Temple Verification</span>
              </div>
            </div>

            <div className="awaiting-approval-notice">
              <h4>⏳ Next Step: Temple Approval</h4>
              <p>
                A confirmation email has been sent to <strong>{submittedBooking.email}</strong>.
                Our temple administration is verifying your payment with temple accounts.
                <strong>Please wait for approval from the temple side.</strong> Once approved, you will receive an official confirmation email.
              </p>
            </div>

            {/* OFFICIAL TEMPLE RECEIPT OPTION FOR ALL POOJAS */}
            <div className="sathya-official-receipt-cta">
              <div className="receipt-cta-header">
                <FaReceipt className="receipt-cta-icon" />
                <div className="receipt-cta-text">
                  <h4>{submittedBooking?.poojaName || formData.poojaName} • அதிகாரப்பூர்வ ரசீது</h4>
                  <p>Official Temple Receipt Slip with devotee sankalpam details and Mandir seal is ready.</p>
                </div>
              </div>
              <button
                type="button"
                className="sathya-open-receipt-btn"
                onClick={() => setShowOfficialReceipt(true)}
              >
                <FaReceipt /> View & Print Official Temple Receipt
              </button>
            </div>

            <div className="pooja-modal-footer" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="modal-primary-btn"
                onClick={onClose}
              >
                Done / Close
              </button>
            </div>
          </div>
        )}
      </div>

      {/* OFFICIAL TEMPLE PRINT RECEIPT FOR ALL POOJAS */}
      {showOfficialReceipt && (
        <SathyanarayanaReceipt
          booking={{
            ...formData,
            ...submittedBooking,
            poojaName: submittedBooking?.poojaName || formData.poojaName,
            devoteeName: submittedBooking?.devoteeName || formData.devoteeName,
            email: submittedBooking?.email || formData.email,
            phoneNumber: submittedBooking?.phoneNumber || formData.phoneNumber,
            address: submittedBooking?.address || formData.address,
            amount: submittedBooking?.amount || formData.amount || 1000,
            poojaDate: submittedBooking?.poojaDate || formData.poojaDate,
            receiptNumber:
              submittedBooking?.receiptNumber ||
              (submittedBooking?.bookingId
                ? String(submittedBooking.bookingId).replace(/\D/g, "").slice(-4)
                : "2300"),
          }}
          isOpen={showOfficialReceipt}
          onClose={() => setShowOfficialReceipt(false)}
        />
      )}
    </div>
  );
};

export default PoojaBookingModal;
